'use client'

import React from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant, navigateToPDP } from '@src/components/product-card/product-card.helpers'
import { STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { trackingMetaData } from '@src/pages/[...slug]/components/collection/collection.constants'
import CollectionFilters from '@src/pages/[...slug]/components/collection/molecules/collectionFilters/CollectionFilters'
import NoProduct from '@src/pages/[...slug]/components/collection/molecules/noProducts'
import { LoaderWrapper, Skeleton } from '@src/quince-components/src'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'

import styles from './miniPDPCollection.module.scss'
import { useMiniPDPCollection } from './useMiniPDPCollection'

const TRACKING_CATEGORY_NAVIGATION = 'navigation'

export function MiniPDPCollection({
  collectionId,
  color,
  productId,
}: Readonly<{
  collectionId: string
  color: string
  productId: string | number
}>) {
  const {
    collectionPage,
    totalProductsCount,
    initialFilters,
    sortBy,
    selectedFilters,
    updateQuery,
    showSubCollectionsPageLoader,
    showNextFeedLoading,
    filteredSubCollection,
    totalProductsCurrentlyOnPage,
    intersectRef,
    productViewTracker,
    filterSectionRef,
    productSectionRef,
    productCardsSectionRef,
    tracker,
    router,
  } = useMiniPDPCollection({
    collectionId,
    color,
    productId,
  })

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP

  const maxWidth = getMaxWidthInPx({
    maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    deviceType: deviceType,
  })

  return (
    <>
      <div id="filter-section" ref={filterSectionRef}>
        <CollectionFilters
          totalProductsCount={totalProductsCount}
          initialFilters={initialFilters}
          sortBy={sortBy}
          selectedFilters={selectedFilters}
          updateQuery={updateQuery}
          collectionPage={collectionPage}
          newCLPFiltersDesktopBucket={STATSIG_EXPERIMENTS_BUCKETS.GROUP_A}
          newCLPFiltersMobileBucket={STATSIG_EXPERIMENTS_BUCKETS.GROUP_A}
          showLoader={showSubCollectionsPageLoader && initialFilters.length === 0}
          showGroupedPage={false}
          subCollectionCards={[]}
          filtersContainerClassName={styles.filtersWrapper}
        />
      </div>
      <div ref={productSectionRef}>
        <div className={styles.subcollection_wrapper}>
          {showSubCollectionsPageLoader ? (
            <div className={styles.productCardsSkeletonWrapper}>
              {new Array(isDesktop ? 8 : 4).fill(0).map((_, index) => (
                <Skeleton key={index} className={styles.productCardSkeleton} />
              ))}
            </div>
          ) : null}
          <div className={styles.productCardsWrapper}>
            {filteredSubCollection?.productCards?.map((productCard, idx) => {
              //TODO Handle runtime refresh of color order
              const initialSelectedVariant = getInitialSelectedVariant({
                cardVariants: productCard.product.cardVariants,
              })
              return (
                <ProductCard
                  key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                  productCard={productCard}
                  className={styles.productCard}
                  optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                  shouldShowMore
                  productViewTracker={productViewTracker}
                  productClickTrackConfig={{
                    trackInternal: true,
                    trackGA: true,
                    category: TRACKING_CATEGORY_NAVIGATION,
                    action: 'click_subcollection_product',
                    context: {
                      product_id: productCard.product.relatedIds.spid,
                      product_color: initialSelectedVariant?.displayConfig.value,
                      product_position: idx,
                      ...trackingMetaData.context,
                    },
                    value: productCard.product.slug,
                  }}
                  onShowMoreClick={({ selectedVariant }) => {
                    navigateToPDP({
                      slug: productCard.product.slug,
                      tracker: tracker && `${tracker}__${idx}`,
                      selectedOption: selectedVariant?.displayConfig.value,
                      router,
                    })
                  }}
                  maxWidth={maxWidth}
                  eagerLoadImage={isDesktop ? idx < 4 : idx < 2}
                  tracker={tracker && `${tracker}__${idx}`}
                  showWhiteGloveDeliveryTag
                />
              )
            })}
          </div>

          <div id="boundary_bottom" className={styles.boundary_bottom} ref={intersectRef} />
          <LoaderWrapper
            testid={TEST_LOCATORS.COLLECTION_INFINITE_LOADER}
            showLoader={showNextFeedLoading}
            // eslint-disable-next-line i18next/no-literal-string
            minHeight={'40rem'}
          >
            <div />
          </LoaderWrapper>
        </div>
      </div>
      {totalProductsCurrentlyOnPage === 0 && !showSubCollectionsPageLoader && (
        <NoProduct
          onResetFilters={() => {
            updateQuery({
              filter: { type: 'reset' },
              sort: { type: 'reset' },
            })
          }}
          isNewCLPFiltersExperimentActive={true}
          ref={productCardsSectionRef}
        />
      )}
    </>
  )
}
